'use client';

import { useEffect } from 'react';
import * as CookieConsent from 'vanilla-cookieconsent';
// Type-only + constant import from a narrow subpath: pulls the consent module
// alone, not the GA loader, so this package does not gain a next/script dependency.
import { CONSENT_CHANGE_EVENT } from '@west-tennessee-consulting/analytics/consent';

import 'vanilla-cookieconsent/dist/cookieconsent.css';
import './styles.css';

/** Consent category keys shared across all WTC apps. */
export const CONSENT_CATEGORY_NECESSARY = 'necessary';
export const CONSENT_CATEGORY_ANALYTICS = 'analytics';

/**
 * Canonical privacy policy for ALL WTC apps. Single source of truth — the page
 * is hosted once in apps/website at `[locale]/privacy/page.tsx` and every other
 * app links to it as an absolute URL. Do not fork per-app privacy pages.
 * See .claude/rules/PRIVACY_POLICY_URL.md.
 */
export const WTC_PRIVACY_POLICY_URL = 'https://www.westtn.consulting/en/privacy';

export interface CookieConsentBannerProps {
  /** Display name used in the banner copy (e.g. "West Tennessee Consulting"). */
  siteName?: string;
  /**
   * Privacy-policy URL rendered in the banner footer. Defaults to the shared
   * WTC privacy policy ({@link WTC_PRIVACY_POLICY_URL}); override only for an
   * app that genuinely has its own policy.
   */
  privacyPolicyUrl?: string;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/**
 * Pushes a Google Consent Mode v2 update reflecting the user's current
 * category choices. Safe on apps without Google Analytics: the entry sits
 * in the dataLayer and is simply never read.
 */
function updateGtagConsent(): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  // gtag must push the IArguments object itself (gtag.js contract).
  function gtag(..._args: unknown[]): void {
    window.dataLayer?.push(arguments);
  }
  const analyticsGranted = CookieConsent.acceptedCategory(CONSENT_CATEGORY_ANALYTICS);
  const value = analyticsGranted ? 'granted' : 'denied';
  gtag('consent', 'update', {
    analytics_storage: value,
    functionality_storage: value,
  });

  // Tell @west-tennessee-consulting/analytics about the decision. Consent Mode alone is not enough: the
  // event layer holds pre-consent events in a queue and needs an explicit signal to
  // release them, otherwise every interaction that happened while this banner was on
  // screen is lost — which is most of the first-touch behavior on a funnel.
  //
  // A DOM event rather than an import keeps the dependency one-way: this package
  // owns consent, @west-tennessee-consulting/analytics owns events, and neither bundles the other's code.
  window.dispatchEvent(
    new CustomEvent(CONSENT_CHANGE_EVENT, { detail: { analytics: value } })
  );
}

/**
 * Cookie-consent banner for all WTC monorepo apps, built on
 * vanilla-cookieconsent v3 (MIT) with Google Consent Mode v2 integration.
 *
 * Render once in the app's root layout, inside <body>:
 *
 * ```tsx
 * import { CookieConsentBanner } from '@west-tennessee-consulting/cookie-consent';
 *
 * <body>
 *   <CookieConsentBanner siteName="West Tennessee Consulting" />
 *   {children}
 * </body>
 * ```
 *
 * Theming: the banner inherits each app's look via CSS variables. The
 * packaged styles.css maps cookie-consent tokens to the app's theme tokens
 * (--background, --foreground, --primary, ...) with neutral fallbacks. Apps
 * whose tokens are not full color values (e.g. raw HSL triples) override the
 * `#cc-main` block in their own globals.css.
 *
 * Consent flow: GA loads with Consent Mode v2 defaults DENIED (see
 * @west-tennessee-consulting/funnel-components GoogleAnalytics). Accepting analytics here flips
 * consent to granted; rejecting keeps GA cookieless. Consent choice is
 * persisted by the library in the `cc_cookie` cookie (consent id + ISO
 * timestamps), which is the auditable consent record.
 */
export function CookieConsentBanner({
  siteName = 'This site',
  privacyPolicyUrl = WTC_PRIVACY_POLICY_URL,
}: CookieConsentBannerProps) {
  useEffect(() => {
    void CookieConsent.run({
      guiOptions: {
        consentModal: {
          layout: 'box inline',
          position: 'bottom right',
          equalWeightButtons: true,
          flipButtons: false,
        },
        preferencesModal: {
          layout: 'box',
          equalWeightButtons: true,
        },
      },
      categories: {
        [CONSENT_CATEGORY_NECESSARY]: {
          enabled: true,
          readOnly: true,
        },
        [CONSENT_CATEGORY_ANALYTICS]: {
          autoClear: {
            cookies: [{ name: /^_ga/ }, { name: '_gid' }],
          },
        },
      },
      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              title: 'We value your privacy',
              description: `${siteName} uses cookies to understand how visitors use the site and to improve your experience. You can accept all cookies or reject the non-essential ones.`,
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject non-essential',
              showPreferencesBtn: 'Manage preferences',
              footer: privacyPolicyUrl
                ? `<a href="${privacyPolicyUrl}" target="_blank" rel="noopener noreferrer">Privacy Policy</a>`
                : undefined,
            },
            preferencesModal: {
              title: 'Cookie preferences',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject non-essential',
              savePreferencesBtn: 'Save preferences',
              closeIconLabel: 'Close',
              sections: [
                {
                  title: 'Strictly necessary cookies',
                  description:
                    'Required for the site to function (authentication, security, remembering your consent choice). Always on.',
                  linkedCategory: CONSENT_CATEGORY_NECESSARY,
                },
                {
                  title: 'Analytics cookies',
                  description:
                    'Help us understand how the site is used (Google Analytics). No personal profiles are built and data is used only to improve the experience.',
                  linkedCategory: CONSENT_CATEGORY_ANALYTICS,
                },
              ],
            },
          },
        },
      },
      onFirstConsent: updateGtagConsent,
      onConsent: updateGtagConsent,
      onChange: updateGtagConsent,
    });
  }, [siteName, privacyPolicyUrl]);

  return null;
}
